'use client';

import { useEffect, useRef, useState } from 'react';
import { MegaMenuDropdown } from './MegaMenuDropdown';

interface MegaMenuWrapperProps {
  columns: any[];
  cta?: any;
  onClose: () => void;
  navRef?: React.RefObject<HTMLElement>;
  onMouseEnter?: () => void;
  onMouseLeave?: (e: React.MouseEvent) => void;
}

export function MegaMenuWrapper({ columns, cta, onClose, navRef, onMouseEnter, onMouseLeave }: MegaMenuWrapperProps) {
  const [navBounds, setNavBounds] = useState({ left: 0, right: 0 });
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Get navigation container bounds for content alignment
    const updateBounds = () => {
      if (navRef?.current) {
        const navRect = navRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        
        // Calculate padding to align with nav container
        setNavBounds({
          left: navRect.left,
          right: viewportWidth - navRect.right
        });
      }
    };

    updateBounds();
    window.addEventListener('resize', updateBounds);
    return () => window.removeEventListener('resize', updateBounds);
  }, [navRef]);

  // Handle click outside - check against actual menu content, not wrapper
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Get the actual menu content element (not the full-width wrapper)
      const menuContent = wrapperRef.current?.querySelector('[data-testid="mega-menu-bridge"]')?.parentElement;
      if (menuContent && !menuContent.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  return (
    <>
      {/* Dark Blur Backdrop - starts below nav bar */}
      <div 
        className={`
          fixed top-16 left-0 right-0 bottom-0 z-40
          transition-all duration-400 ease-out
          opacity-100
        `}
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          pointerEvents: 'none'
        }}
      />
      
      {/* Full viewport width wrapper - non-interactive for positioning only */}
      <div 
        ref={wrapperRef}
        className="fixed left-0 right-0 top-16 z-50 pointer-events-none"
        style={{
          paddingLeft: `${navBounds.left}px`,
          paddingRight: `${navBounds.right}px`
        }}
      >
        {/* Interactive content wrapper - only the menu area is interactive */}
        <div 
          className="relative pointer-events-auto max-w-[1024px] mx-auto"
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        >
          {/* Invisible bridge to maintain hover state - DO NOT REMOVE OR MENU BREAKS */}
          {/* This invisible div maintains hover state between menu trigger and dropdown */}
          <div 
            className="absolute -top-2 -bottom-2 left-0 right-0 bg-transparent"
            data-testid="mega-menu-bridge"
          />
          {/* Desktop mega menu dropdown */}
          <MegaMenuDropdown 
            columns={columns}
            cta={cta}
            onItemClick={onClose}
            position={{
              visibility: 'visible'
            }}
          />
        </div>
      </div>

    </>
  );
}